import { Injectable, WritableSignal, signal, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { BarcodeScanner } from '@capacitor-mlkit/barcode-scanning';
import { UiFeedbackService } from './ui-feedback.service';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

export type PermissionStatus = 'granted' | 'prompt' | 'prompt-with-rationale' | 'denied' | 'permanently-denied' | 'unavailable';

export interface PermissionDescriptor {
  id: string;
  checkNative(): Promise<PermissionStatus>;
  requestNative(): Promise<PermissionStatus>;
  checkWeb(): Promise<PermissionStatus>;
  requestWeb(): Promise<PermissionStatus>;
  platforms: ('native' | 'web')[];
  rationaleTitleKey?: string;
  rationaleMessageKey?: string;
  settingsSupported: boolean;
}

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private uiFeedback = inject(UiFeedbackService);
  private alertController = inject(AlertController);
  private translateService = inject(TranslateService);

  private isNative = Capacitor.isNativePlatform();
  private hasRequestedCamera = false;
  private signalCache = new Map<string, WritableSignal<PermissionStatus>>();

  private descriptors = new Map<string, PermissionDescriptor>();

  constructor() {
    this.registerCamera();
    this.registerStoragePersistence();
  }

  private registerCamera() {
    this.descriptors.set('camera', {
      id: 'camera',
      platforms: ['native', 'web'],
      settingsSupported: this.isNative,
      rationaleTitleKey: 'PERMISSIONS.CAMERA_RATIONALE_TITLE',
      rationaleMessageKey: 'PERMISSIONS.CAMERA_RATIONALE_MESSAGE',

      checkNative: async () => {
        const { camera } = await BarcodeScanner.checkPermissions();
        return this.mapCameraState(camera, this.hasRequestedCamera);
      },

      requestNative: async () => {
        const { camera } = await BarcodeScanner.requestPermissions();
        const result = this.mapCameraState(camera, this.hasRequestedCamera);
        this.hasRequestedCamera = true;
        return result;
      },

      checkWeb: async () => {
        try {
          const perm = await navigator.permissions.query({ name: 'camera' as PermissionName });
          switch (perm.state) {
            case 'granted': return 'granted';
            case 'denied': return 'denied';
            case 'prompt': return 'prompt';
            default: return 'unavailable';
          }
        } catch {
          return 'prompt';
        }
      },

      requestWeb: async () => {
        return 'prompt';
      },
    });
  }

  private registerStoragePersistence() {
    this.descriptors.set('storage-persistence', {
      id: 'storage-persistence',
      platforms: ['native', 'web'],
      settingsSupported: false,

      checkNative: async () => 'granted',

      requestNative: async () => 'granted',

      checkWeb: async () => {
        if (typeof navigator.storage === 'undefined' || !navigator.storage?.persisted) {
          return 'unavailable';
        }
        try {
          const persisted = await navigator.storage.persisted();
          return persisted ? 'granted' : 'prompt';
        } catch {
          return 'unavailable';
        }
      },

      requestWeb: async () => {
        if (typeof navigator.storage === 'undefined' || !navigator.storage?.persist) {
          return 'unavailable';
        }
        try {
          const persisted = await navigator.storage.persist();
          return persisted ? 'granted' : 'denied';
        } catch {
          return 'unavailable';
        }
      },
    });
  }

  get availablePermissions(): string[] {
    return Array.from(this.descriptors.keys());
  }

  private getSignal(id: string): WritableSignal<PermissionStatus> {
    let sig = this.signalCache.get(id);
    if (!sig) {
      sig = signal<PermissionStatus>('unavailable');
      this.signalCache.set(id, sig);
    }
    return sig;
  }

  status$(id: string): WritableSignal<PermissionStatus> {
    return this.getSignal(id);
  }

  async check(id: string): Promise<PermissionStatus> {
    const desc = this.descriptors.get(id);
    if (!desc) return 'unavailable';

    const status = this.isNative && desc.platforms.includes('native')
      ? await desc.checkNative()
      : !this.isNative && desc.platforms.includes('web')
        ? await desc.checkWeb()
        : 'unavailable';

    this.getSignal(id).set(status);
    return status;
  }

  async request(id: string): Promise<PermissionStatus> {
    const desc = this.descriptors.get(id);
    if (!desc) return 'unavailable';

    const status = this.isNative && desc.platforms.includes('native')
      ? await desc.requestNative()
      : !this.isNative && desc.platforms.includes('web')
        ? await desc.requestWeb()
        : 'unavailable';

    this.getSignal(id).set(status);
    return status;
  }

  async ensure(id: string): Promise<boolean> {
    const desc = this.descriptors.get(id);
    if (!desc) return false;

    let status = await this.check(id);

    if (status === 'granted') return true;

    if (status === 'prompt-with-rationale') {
      await this.uiFeedback.showAlert({
        headerKey: desc.rationaleTitleKey ?? 'PERMISSIONS.CAMERA_RATIONALE_TITLE',
        messageKey: desc.rationaleMessageKey ?? 'PERMISSIONS.CAMERA_RATIONALE_MESSAGE',
      });
      status = await this.request(id);
      if (status === 'granted') return true;
      if (status === 'permanently-denied') {
        await this.showPermanentlyDeniedAlert(id);
        return false;
      }
      if (status === 'denied') {
        await this.uiFeedback.showAlert({
          headerKey: 'PERMISSIONS.CAMERA_DENIED_TITLE',
          messageKey: 'PERMISSIONS.CAMERA_DENIED_MESSAGE',
        });
        return false;
      }
      return false;
    }

    if (status === 'prompt') {
      status = await this.request(id);
      if (status === 'granted') return true;
      if (status === 'permanently-denied') {
        await this.showPermanentlyDeniedAlert(id);
        return false;
      }
      if (status === 'denied') {
        await this.uiFeedback.showAlert({
          headerKey: 'PERMISSIONS.CAMERA_DENIED_TITLE',
          messageKey: 'PERMISSIONS.CAMERA_DENIED_MESSAGE',
        });
        return false;
      }
      return false;
    }

    if (status === 'denied') {
      await this.uiFeedback.showAlert({
        headerKey: 'PERMISSIONS.CAMERA_DENIED_TITLE',
        messageKey: 'PERMISSIONS.CAMERA_DENIED_MESSAGE',
      });
      return false;
    }

    if (status === 'permanently-denied') {
      await this.showPermanentlyDeniedAlert(id);
      return false;
    }

    if (status === 'unavailable') {
      await this.uiFeedback.showToast('PERMISSIONS.ESTIMATE_UNAVAILABLE');
      return false;
    }

    return false;
  }

  async getEstimate(): Promise<{ usage: number; quota: number } | null> {
    if (typeof navigator.storage === 'undefined' || !navigator.storage?.estimate) {
      return null;
    }
    try {
      const est = await navigator.storage.estimate();
      return { usage: est.usage ?? 0, quota: est.quota ?? 0 };
    } catch {
      return null;
    }
  }

  private async showPermanentlyDeniedAlert(id: string) {
    const isNativeCamera = id === 'camera' && this.isNative;

    const alert = await this.alertController.create({
      header: this.translateService.instant('PERMISSIONS.CAMERA_BLOCKED_TITLE') as string,
      message: this.translateService.instant(
        isNativeCamera ? 'PERMISSIONS.CAMERA_BLOCKED_MESSAGE' : 'PERMISSIONS.CAMERA_BLOCKED_PWA_MESSAGE',
      ) as string,
      buttons: [
        {
          text: this.translateService.instant('COMMON.CANCEL') as string,
          role: 'cancel',
        },
        {
          text: this.translateService.instant('PERMISSIONS.ACTIONS.OPEN_SETTINGS') as string,
          handler: () => {
            if (isNativeCamera) {
              BarcodeScanner.openSettings();
            }
          },
        },
      ],
    });
    await alert.present();
  }

  private mapCameraState(state: string, previouslyRequested: boolean): PermissionStatus {
    if (state === 'granted' || state === 'limited') return 'granted';
    if (state === 'prompt') return 'prompt';
    if (state === 'prompt-with-rationale') return 'prompt-with-rationale';
    if (state === 'denied') {
      return previouslyRequested ? 'permanently-denied' : 'denied';
    }
    return 'unavailable';
  }
}
